import { applyKlaviyoBisButtonState } from 'util.klaviyo-bis'

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class extends HTMLElement {
      constructor() {
        super()
        this.onVariantChange = this.onVariantChange.bind(this)
        this.onChangeOptions = this.onChangeOptions.bind(this)
        this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this)
        this.syncPageOffset = this.syncPageOffset.bind(this)
      }

      connectedCallback() {
        this.observer?.disconnect()
        this.abortController?.abort()
        this.abortController = new AbortController()
        this.sectionId = this.dataset.sectionId
        this.productId = this.dataset.productId
        this.button = this.querySelector('[data-sticky-atc-button]')
        this.buttonText = this.button && this.button.querySelector('span')
        this.priceSlot = this.querySelector('[data-sticky-price]')
        this.optionsButton = this.querySelector('[data-sticky-options]')
        this.optionsText = this.querySelector('[data-sticky-options-text]')
        this.variantLoading = false
        this.buttonDisabledBeforeLoading = this.button?.disabled ?? false
        this.mql = window.matchMedia('(max-width: 768px)')
        this.anchor =
          document.getElementById('ProductSubmitButton-' + this.sectionId) ||
          document.querySelector('block-buy-buttons[data-section-id="' + this.sectionId + '"]')
        this.lastIntersectionEntry = null
        this.setAttribute('aria-hidden', 'true')

        this.eventName = 'variant:change:' + this.sectionId + ':' + this.productId
        document.addEventListener(this.eventName, this.onVariantChange, { signal: this.abortController.signal })
        this.optionsButton?.addEventListener('click', this.onChangeOptions, {
          signal: this.abortController.signal
        })
        this.mql.addEventListener('change', this.handleMediaQueryChange, {
          signal: this.abortController.signal
        })
        window.addEventListener('resize', this.syncPageOffset, { signal: this.abortController.signal })

        if (this.optionsButton && !this.findVariantPicker()) {
          this.optionsButton.hidden = true
        }

        if (this.button && this.anchor && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => this.onIntersect(entries[0]), {
            threshold: [0, 0.5, 0.85, 1]
          })
          this.observer.observe(this.anchor)
        } else {
          this.setVisible(false)
        }
      }

      disconnectedCallback() {
        if (this.variantLoading && this.button) {
          this.button.disabled = this.buttonDisabledBeforeLoading
          this.button.removeAttribute('aria-busy')
        }
        this.variantLoading = false
        this.setVisible(false)
        this.observer && this.observer.disconnect()
        this.abortController?.abort()
        this.clearPageOffset()
        this.observer = null
        this.abortController = null
      }

      onIntersect(entry) {
        if (!entry) return
        this.lastIntersectionEntry = entry

        if (!this.mql.matches || !this.canShowPurchaseDock()) {
          this.setVisible(false)
          return
        }

        const ratio = Number.isFinite(entry.intersectionRatio)
          ? entry.intersectionRatio
          : entry.isIntersecting
            ? 1
            : 0

        if (ratio < 0.5) {
          this.setVisible(true)
        } else if (ratio >= 0.85) {
          this.setVisible(false)
        }
      }

      handleMediaQueryChange() {
        if (this.lastIntersectionEntry) {
          this.onIntersect(this.lastIntersectionEntry)
          return
        }

        this.setVisible(false)
      }

      canShowPurchaseDock() {
        const hasVariantAccess = !this.optionsButton || Boolean(this.findVariantPicker())

        return Boolean(
          this.button &&
            this.anchor &&
            this.observer &&
            hasVariantAccess &&
            !this.button.hasAttribute('klaviyo-bis-trigger')
        )
      }

      reevaluateVisibility() {
        if (this.lastIntersectionEntry) {
          this.onIntersect(this.lastIntersectionEntry)
          return
        }

        this.setVisible(false)
      }

      setVisible(show) {
        const visible = Boolean(show && this.canShowPurchaseDock())
        this.classList.toggle('sticky-atc--visible', visible)
        this.setAttribute('aria-hidden', String(!visible))

        if (visible) {
          this.syncPageOffset()
        } else {
          this.clearPageOffset()
        }
      }

      syncPageOffset() {
        if (!this.mql?.matches || !this.classList.contains('sticky-atc--visible')) return

        const height = Math.ceil(this.getBoundingClientRect().height)
        if (!height) return

        document.documentElement.style.setProperty('--sticky-atc-height', `${height}px`)
        document.documentElement.classList.add('sticky-atc-is-visible')
      }

      clearPageOffset() {
        const visibleSticky = document.querySelector('sticky-add-to-cart.sticky-atc--visible')
        if (visibleSticky && visibleSticky !== this) return

        document.documentElement.classList.remove('sticky-atc-is-visible')
        document.documentElement.style.removeProperty('--sticky-atc-height')
      }

      findVariantPicker() {
        return document.querySelector('kit-variant-picker[data-section-id="' + this.sectionId + '"]')
      }

      onChangeOptions() {
        const picker = this.findVariantPicker()
        if (!picker) return

        const focusTarget =
          picker.querySelector('input[type="radio"]:checked:not([disabled])') ||
          picker.querySelector('select:not([disabled])') ||
          picker.querySelector('input[type="radio"]:not([disabled])')
        if (!focusTarget) return

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        picker.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          block: 'center'
        })

        try {
          focusTarget.focus({ preventScroll: true })
        } catch (error) {
          focusTarget.focus()
        }
      }

      onVariantChange(event) {
        const detail = event.detail || {}
        if (!this.button) return

        if (detail.loading) {
          if (!this.variantLoading) {
            this.buttonDisabledBeforeLoading = this.button.disabled
          }
          this.variantLoading = true
          this.button.disabled = true
          this.button.setAttribute('aria-busy', 'true')
          return
        }
        this.variantLoading = false
        this.button.removeAttribute('aria-busy')

        const variant = detail.variant
        if (!variant) {
          applyKlaviyoBisButtonState(this.button, {
            active: false,
            inactiveType: 'submit',
            inactiveDisabled: true
          })
          if (this.buttonText && this.button.dataset.unavailable) {
            this.buttonText.textContent = this.button.dataset.unavailable
          }
          this.syncVariantOptions(null)
          this.reevaluateVisibility()
          return
        }

        const renderedButton = detail.html && detail.html.querySelector('[data-sticky-atc-button]')
        if (renderedButton) {
          this.applyRenderedButtonState(renderedButton)
        } else {
          this.applyVariantFallbackState(variant)
        }

        this.syncVariantOptions(variant)

        // Mirror the freshly rendered price from the fetched section markup.
        if (detail.html && this.priceSlot) {
          try {
            const stickySource = detail.html.querySelector(
              'sticky-add-to-cart[data-section-id="' + this.sectionId + '"] [data-sticky-price]'
            )
            const fetched = detail.html.querySelector('block-price[data-section-id="' + this.sectionId + '"]')
            const blockSource = fetched?.querySelector(
              fetched.hasAttribute('data-on-sale') ? '.block-price__sale' : '.block-price__regular'
            )
            const source = stickySource || blockSource

            if (source) {
              const clone = source.cloneNode(true)
              clone.querySelectorAll('.element-text--visually-hidden').forEach((el) => el.remove())
              const text = clone.textContent.replace(/\s+/g, ' ').trim()
              if (text) this.priceSlot.textContent = text
            }
          } catch (e) {
            /* keep the current price if the fetched markup can't be parsed */
          }
        }

        this.reevaluateVisibility()
      }

      getVariantOptionText(variant) {
        if (!variant) return ''

        const selectedOptions = Array.isArray(variant.selectedOptions)
          ? variant.selectedOptions.map((option) => option?.value)
          : []
        const options = Array.isArray(variant.options) ? variant.options : []
        const values = (selectedOptions.length ? selectedOptions : options).filter(Boolean)
        if (values.length) return values.join(' / ')

        const title = typeof variant.title === 'string' ? variant.title.trim() : ''
        return title && title !== 'Default Title' ? title : ''
      }

      syncVariantOptions(variant) {
        if (!this.optionsButton || !this.optionsText) return

        const text = this.getVariantOptionText(variant)
        const picker = this.findVariantPicker()
        this.optionsButton.hidden = !text || !picker
        if (!text || !picker) return

        this.optionsText.textContent = text
        const label = this.optionsButton.dataset.stickyOptionsLabel
        if (label) {
          this.optionsButton.setAttribute('aria-label', `${label}: ${text}`)
        }
      }

      applyRenderedButtonState(renderedButton) {
        const renderedText = renderedButton.querySelector('span')
        const variantId = renderedButton.dataset.klaviyoBisVariantId
        const backInStock = renderedButton.hasAttribute('klaviyo-bis-trigger')

        if (variantId) {
          this.button.setAttribute('data-klaviyo-bis-variant-id', variantId)
          this.button.setAttribute('data-variant-id', variantId)
        }

        if (backInStock) {
          applyKlaviyoBisButtonState(this.button, {
            active: true,
            variantId
          })
        } else {
          applyKlaviyoBisButtonState(this.button, {
            active: false,
            inactiveType: renderedButton.getAttribute('type') || 'submit',
            inactiveDisabled: renderedButton.hasAttribute('disabled')
          })
        }

        if (this.buttonText && renderedText) {
          this.buttonText.textContent = renderedText.textContent.replace(/\s+/g, ' ').trim()
        }
      }

      isVariantAvailable(variant) {
        if ('available' in variant) return Boolean(variant.available)
        if ('availableForSale' in variant) return Boolean(variant.availableForSale)
        return false
      }

      applyVariantFallbackState(variant) {
        const available = this.isVariantAvailable(variant)
        applyKlaviyoBisButtonState(this.button, {
          active: false,
          variantId: variant.id,
          inactiveType: 'submit',
          inactiveDisabled: !available
        })

        if (this.buttonText) {
          this.buttonText.textContent = available
            ? this.button.dataset.add
            : this.button.dataset.soldOut
        }
      }
    }
  )
}
